/**
 * Access vocabulary — the ONE place brand access is spelled.
 *
 * A person in an organization is in exactly one of two states:
 *
 *   - Org admin (Clerk `org:admin`, see `./claims`): the organization control
 *     plane (`org.*` capabilities — team, billing, settings, brands) and every
 *     brand, current and future, with no rows anywhere.
 *   - Brand access (Clerk `org:member`): the specific brands an admin granted,
 *     one `memberRoles` row per brand. Inside a granted brand they can do
 *     everything the brand offers; outside it the brand does not exist for them.
 *
 * There is no read-only tier and no org-wide grant: access to a brand is
 * `member`, the only brand role, and the type keeps that name so a grant, an
 * actor and a guard all say the same word. Org role dominates: the control
 * plane is admin-only.
 *
 * This module deliberately has no framework or server dependencies so Convex
 * guards, both Next.js apps, the API/MCP layer, client hooks, and tests all
 * evaluate exactly the same rules.
 */

export const BRAND_ROLES = ['member'] as const

export type BrandRole = (typeof BRAND_ROLES)[number]

/** Org admins hold brand access implicitly on every brand. */
export const ORG_ADMIN_BRAND_ROLE: BrandRole = 'member'

/**
 * How an organization member reaches brands: every brand of the
 * organization, current and future (`all`), or exactly the brands their
 * grants name (`selected`). Chosen when the person is invited and editable by
 * an admin. A person added outside Brew's invitation flow, or before it
 * existed, is `all`; an org admin carries `all` so a later demotion keeps
 * every brand.
 */
export type MemberBrandAccess = 'all' | 'selected'

/** Parse an untrusted brand-access value (provider metadata). Fails closed. */
export function parseMemberBrandAccess(
  value: unknown
): MemberBrandAccess | null {
  return value === 'all' || value === 'selected' ? value : null
}

/** One stored grant: access to the brand `brandId` names. */
export type BrandRoleGrant = Readonly<{
  brandId: string
  role: BrandRole
}>

/** Everything needed to answer "may this person act here" without I/O. */
export type BrandRoleSnapshot = Readonly<{
  isOrgAdmin: boolean
  /** A member on `all` brands (or the organization's own service). */
  everyBrand: boolean
  grants: ReadonlyArray<BrandRoleGrant>
}>

/**
 * Resolve brand access.
 *
 * For one brand (`brandId` given): org admin → access; every-brand access →
 * access; a grant for that brand → access; else `null`.
 *
 * For the org level (`brandId: null`, used by org-scoped actors such as an
 * org API key or the brand-list read): org admin → access; every-brand
 * access or any grant at all → access, so brand discovery stays reachable;
 * else `null`.
 */
export function resolveEffectiveBrandRole(args: {
  isOrgAdmin: boolean
  everyBrand: boolean
  grants: ReadonlyArray<BrandRoleGrant>
  brandId: string | null
}): BrandRole | null {
  if (args.isOrgAdmin || args.everyBrand) {
    return ORG_ADMIN_BRAND_ROLE
  }
  const reaches = args.grants.some(
    (grant) => args.brandId === null || grant.brandId === args.brandId
  )
  return reaches ? 'member' : null
}

/**
 * Named product operations. Every surface consumes this policy: a `member`
 * capability needs access to the brand in question, an `org_admin` capability
 * needs the organization role.
 */
export const CAPABILITY_MIN_ROLE = {
  'brand.read': 'member',
  'emails.read': 'member',
  /** Published Brew gallery only; private templates remain emails.read. */
  'templates.read': 'member',
  'emails.export': 'member',
  'comments.read': 'member',
  'comments.create': 'member',
  'comments.manage_own': 'member',
  'sends.read': 'member',
  'automations.read': 'member',
  'contacts.read': 'member',
  'contacts.export': 'member',
  'audiences.read': 'member',
  'analytics.read': 'member',
  'insights.read': 'member',
  'chat.read': 'member',
  'domains.read': 'member',
  'integrations.read': 'member',
  'mcp.connect': 'member',
  'data.read': 'member',
  'support.send': 'member',
  'brand.edit': 'member',
  'emails.edit': 'member',
  'comments.moderate': 'member',
  'sends.manage': 'member',
  'automations.manage': 'member',
  'contacts.manage': 'member',
  'audiences.manage': 'member',
  'insights.run': 'member',
  'chat.run': 'member',
  'domains.manage': 'member',
  'integrations.manage': 'member',
  /** Brand-scoped API keys: mint, rotate, and revoke keys bound to the brand. */
  'credentials.manage': 'member',
  'org.brands': 'org_admin',
  'org.billing': 'org_admin',
  'org.ai': 'org_admin',
  'org.members': 'org_admin',
  'org.credentials': 'org_admin',
  'org.settings': 'org_admin',
} as const

export type Capability = keyof typeof CAPABILITY_MIN_ROLE
export const CAPABILITIES = Object.keys(
  CAPABILITY_MIN_ROLE
) as Array<Capability>

export function isOrgCapability(capability: Capability): boolean {
  return CAPABILITY_MIN_ROLE[capability] === 'org_admin'
}

/** What a gate needs to know about the caller for one brand. */
export type RbacActor = Readonly<{
  isOrgAdmin: boolean
  /** Access on the brand in question; `null` = no access. */
  brandRole: BrandRole | null
}>

/** The single yes/no answer every surface asks. */
export function can(actor: RbacActor, capability: Capability): boolean {
  if (isOrgCapability(capability)) {
    return actor.isOrgAdmin
  }
  return actor.isOrgAdmin || actor.brandRole !== null
}
